import { groupByKey, calculateTextingStatus } from '../../utils/texting';
import React, { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { Skeleton } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  formatDateDisplayLocal,
  generateStandardFilename,
} from '@fortress-technology-solutions/fortress-component-library/utils';

import { useFetchConversations } from '../../hooks/data-fetching/useFetchConversations';
import useHasPermission from '../../hooks/useHasPermission';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { toastr } from 'react-redux-toastr';
import { calculateRentWithAmenities } from '../../utils/lease-helpers';
import {
  AMENITY_DETAILS_PREFIX,
  HEADERS,
  PROPERTY_PATH_MAP,
  RENT_ROLL_TOTAL_COLUMNS,
} from './constantsV2';
import {
  buildCSVFooterRows,
  buildFloorPlanSummaryForCSVExport,
  FLOOR_PLAN_SUMMARY_CSV_HEADERS,
} from './utilsV2';

import UnitService from '../../services/unitService';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useNewColumnsVisibleByDefault from '../../hooks/useNewColumnsVisibleByDefault';
import {
  accessibilityTypeCell,
  adaDisplayFields,
  ADA_COLUMN_IDS,
  shouldForceADAColumnOrder,
} from '../../utils/ada-helpers';
import { TAB_KEYS } from '../ResidentProfile/constants';
import { RD_PROGRAM_NAME } from '../../constants/affordableProgramNames';
import messages from './messages';
import useAppFlags from 'utils/featureFlags';
import { adaptHeaders } from 'utils/table-helpers';

export const useManageRentRoll = ({
  selectedProperty,
  NAME,
  intl,
  redirectedFromHomeKPI = false,
}) => {
  const propertyClass = selectedProperty?.propertyClass?.propertyType;
  const isRDRAEnabled = selectedProperty?.setup?.isRDRAEnabled ?? false;
  const isAffordable = propertyClass === 'Affordable';
  const isCommercialMixed = propertyClass === 'Commercial-Mixed';
  const isMixed = propertyClass === 'Mixed' || isCommercialMixed;
  const isConventional = propertyClass === 'Conventional';
  const initialName = `${NAME}${redirectedFromHomeKPI ? '-from-KPI' : ''}`;
  const name = useUniqueTableName(initialName);
  const hasRD = selectedProperty?.pap?.some(
    (pafp) => pafp?.masterAffordableProgram?.name === RD_PROGRAM_NAME,
  );

  const rdRents = selectedProperty?.setup?.rdProjectType?.rdrents;
  const flags = useFlags();

  const { units, isLoading } = useFetchManageRentRoll({ selectedProperty });

  const twoWayRecipientIds = useMemo(
    () => units.map((r) => r.twoWayRecipientIds).flat(),
    [units],
  );

  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const showTextingColumn =
    selectedProperty.isTwoWayCommunicationActive &&
    hasCommunicationCreatePermission;

  const [conversations] = useFetchConversations(
    twoWayRecipientIds,
    showTextingColumn,
  );
  const conversationsDictionary = useMemo(() => {
    return groupByKey(conversations, (c) => c.recipient.customerId);
  }, [conversations]);
  const hasFortressAsPaymentProvider = useMemo(
    () =>
      selectedProperty.paymentProviders?.find(
        (pp) => pp.paymentProvider === 'FORTRESS',
      ) !== undefined,
    [selectedProperty],
  );
  const isPropertyFromCanada = selectedProperty.physicalCountry === 'CA';
  const {
    paymentStatusesByUnitId,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useFetchRentRollPaymentStatuses({
    selectedProperty,
    enabled: hasFortressAsPaymentProvider,
    intl,
  });
  const { results, unitAmenityHeaders } = useParseResults({
    units,
    conversationsDictionary,
    paymentStatusesByUnitId,
  });
  const showAutopayColumn = hasFortressAsPaymentProvider;
  const showPADColumn = hasFortressAsPaymentProvider && isPropertyFromCanada;

  const filterOptions = useGetFilterOptions({ results });

  const headers = useHeaders({
    isAffordable,
    isConventional,
    isMixed,
    isRDRAEnabled,
    hasRD,
    rdRents,
    filterOptions,
    unitAmenityHeaders,
    showTextingColumn,
    showAutopayColumn,
    showPADColumn,
    intl,
  });

  const tableFilterSortSearchManagerPayload = useMemo(() => {
    const payload = { name, initialOrderBy: 'unit', headers };
    if (redirectedFromHomeKPI) {
      payload.initialFilterState = {
        underEviction: ['yes'],
      };
    }
    return payload;
  }, [redirectedFromHomeKPI, name, headers]);

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager(tableFilterSortSearchManagerPayload);

  const propertyPathMap = useMemo(
    () => PROPERTY_PATH_MAP({ unitAmenityHeaders }),
    [unitAmenityHeaders],
  );
  const sortedAndFilteredResults = useTableFilterSortData({
    results,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: propertyPathMap,
  });

  const rows = useRows({
    sortedAndFilteredResults,
    unitAmenityHeaders,
    isAffordable,
    isMixed,
    isRDRAEnabled,
    rdRents,
    isPaymentsLoading,
    isPaymentsError,
  });

  // Both memos work together to handle the lifecycle of adding new columns
  // while maintaining a stable drag-and-drop experience

  // Create initial column order to prevent drag-and-drop snap back issue when flags are toggled on/off
  const initialColumnsOrder = useMemo(() => {
    const columnOrder = {};
    headers.forEach((header, index) => {
      columnOrder[header.id] = index;
    });
    return columnOrder;
  }, [headers]);

  // Check if localStorage has the new columns, if not force overwrite. This will prevent ordering conflicts.
  const shouldForceOverwrite = useMemo(() => {
    if (
      shouldForceADAColumnOrder({
        tableName: name,
        showADAColumns: flags.adaInfo,
      })
    ) {
      return true;
    }

    try {
      const storedOrder = localStorage.getItem(`${name}_column-order`);
      if (!storedOrder) return true;

      // Lead source column is only visible when this flag is on
      if (flags.viewMultipleLeadSourcesResidents) {
        const parsedOrder = JSON.parse(storedOrder);
        const newColumnIds = ['leadSources'];

        // If any new column is missing from stored order, force overwrite
        return newColumnIds.some(
          (columnId) => parsedOrder[columnId] === undefined,
        );
      }

      return false;
    } catch {
      return true;
    }
  }, [name, flags.adaInfo, flags.viewMultipleLeadSourcesResidents]);

  // A saved column visibility that predates a column keeps it hidden; show it
  // once, without undoing the columns the user has hidden since.
  const {
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    shouldForceOverwrite: forceOverwrite,
  } = useNewColumnsVisibleByDefault({
    name,
    headers,
    introducedColumnIds: ADA_COLUMN_IDS,
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    forceOverwrite,
  });

  const footerRow = useGetFooterRow({
    filteredHeaders,
    sortedAndFilteredResults,
  });

  const { onCSVButtonClick } = useCSVPDFExport({
    propertyName: selectedProperty?.name,
    hasAnyFilters:
      Object.keys(filterState)?.length ||
      Object.keys(dateState)?.length ||
      Object.keys(searchState)?.length,
    filteredHeaders,
    rows,
    results: sortedAndFilteredResults,
    footerRow,
    isPaymentsLoading,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    isLoading,
    name,
    onCSVButtonClick,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount: results?.length ?? undefined,
  };
};

const useFetchManageRentRoll = ({ selectedProperty = {} }) => {
  const { id, organizationId } = selectedProperty;
  const abortController = new AbortController();
  const queryKey = [id, organizationId];
  const options = { signal: abortController.signal };
  const { data, isLoading } = useQuery(
    ['manageRentRoll', queryKey],
    () => new UnitService().getRentRoll(id, organizationId, options),
    {
      enabled: Boolean(id.length && organizationId.length),
      refetchOnWindowFocus: false,
      onError: (e) => {
        toastr.error('Error Loading Rent Roll', e.toString());
      },
    },
  );
  const units = useMemo(() => data?.units ?? [], [data?.units]);

  return {
    units,
    isLoading,
  };
};

export const useFetchRentRollPaymentStatuses = ({
  selectedProperty = {},
  enabled = false,
  intl,
}) => {
  const { id, organizationId } = selectedProperty;
  const { data, isLoading, isError } = useQuery(
    ['manageRentRoll', 'paymentStatuses', id, organizationId],
    () => new UnitService().getRentRollPaymentStatuses(id, organizationId),
    {
      enabled: Boolean(enabled && id?.length && organizationId?.length),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      onError: () => {
        toastr.error(
          intl.formatMessage(messages.paymentStatusesErrorHeader),
          intl.formatMessage(messages.paymentStatusesErrorMessage),
        );
      },
    },
  );
  const paymentStatusesByUnitId = useMemo(
    () => data?.paymentStatusesByUnitId ?? {},
    [data?.paymentStatusesByUnitId],
  );

  return {
    paymentStatusesByUnitId,
    isLoading: enabled && isLoading,
    isError: Boolean(isError),
  };
};

const useGetFilterOptions = ({ results }) => {
  return useMemo(() => {
    const options = {
      bedsBathsOptions: [],
      floorPlanOptions: [],
      statusOptions: [],
      applicantStatusOptions: [],
      buildingNumberOptions: [],
      leadSourcesOptions: [],
    };

    if (results) {
      results.forEach(
        ({
          floorPlan,
          bedsBaths,
          status,
          applicantStatus,
          buildingNumber,
          leadSources,
        }) => {
          if (floorPlan?.length) options.floorPlanOptions.push(floorPlan);
          if (bedsBaths?.length) options.bedsBathsOptions.push(bedsBaths);
          if (status?.length) options.statusOptions.push(status);
          if (applicantStatus?.length)
            options.applicantStatusOptions.push(applicantStatus);
          if (buildingNumber?.length)
            options.buildingNumberOptions.push(buildingNumber);
          if (leadSources?.length) {
            // Split comma-separated lead sources and add each one
            const sources = leadSources
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            options.leadSourcesOptions.push(...sources);
          }
        },
      );

      options.floorPlanOptions = Array.from(new Set(options.floorPlanOptions))
        .sort()
        .map((floorPlan) => ({
          text: floorPlan,
          value: floorPlan?.toLowerCase(),
        }));

      options.bedsBathsOptions = Array.from(new Set(options.bedsBathsOptions))
        .sort()
        .map((bedsBaths) => ({
          text: bedsBaths,
          value: bedsBaths,
        }));

      options.statusOptions = Array.from(new Set(options.statusOptions))
        .sort()
        .map((status) => ({
          text: status,
          value: status?.toLowerCase(),
        }));

      options.applicantStatusOptions = Array.from(
        new Set(options.applicantStatusOptions),
      )
        .sort()
        .map((applicantStatus) => ({
          text: applicantStatus,
          value: applicantStatus?.toLowerCase(),
        }));

      options.buildingNumberOptions = Array.from(
        new Set(options.buildingNumberOptions),
      )
        .sort((a, b) => a - b)
        .map((buildingNumber) => ({
          text: buildingNumber,
          value: buildingNumber.toLowerCase(),
        }));

      options.leadSourcesOptions = Array.from(
        new Set(options.leadSourcesOptions),
      )
        .sort()
        .map((leadSource) => ({
          text: leadSource,
          value: leadSource.toLowerCase(),
        }));
    }

    return options;
  }, [results]);
};

const useGetFooterRow = ({ filteredHeaders, sortedAndFilteredResults }) => {
  return useMemo(() => {
    const totalsColumns = JSON.parse(JSON.stringify(RENT_ROLL_TOTAL_COLUMNS));

    sortedAndFilteredResults.forEach((result) => {
      Object.keys(totalsColumns).forEach((id) => {
        if (result?.[id]) {
          const addition =
            typeof result[id] === 'string'
              ? parseFloat(result[id])
              : result[id];
          totalsColumns[id].value += addition;
        }
      });
    });

    return filteredHeaders.reduce((object, { id }) => {
      const isSqFt = id === 'sqFt';
      object[id] = totalsColumns[id]
        ? {
            value: totalsColumns[id].value,
            variant: isSqFt ? 'number' : 'currency',
          }
        : { value: '' };
      return object;
    }, {});
  }, [filteredHeaders, sortedAndFilteredResults]);
};

// Payment display values, shaped like the other per-row derivations below.
// Object.assign ignores null, so a unit with no payment statuses is a no-op.
const paymentDisplayFields = (statuses) =>
  statuses
    ? {
        isAutopayActive: statuses.isAutopayActive ? 'on' : 'off',
        isPADActive: statuses.isPADActive ? 'on' : 'off',
      }
    : null;

const useParseResults = ({
  units,
  conversationsDictionary,
  paymentStatusesByUnitId = {},
}) => {
  return useMemo(() => {
    let results = [];
    let unitAmenityHeaders = new Set();

    units.forEach((result) => {
      const parsedResult = { ...result };

      // Apply unit-level pricing logic for quotingRent
      // Always recalculate to handle amenities properly
      const quotingRentToUse = result.quotingRentAmount ?? 0;

      // Always recalculate with amenities
      parsedResult.quotingRent = calculateRentWithAmenities(
        result,
        quotingRentToUse,
      );

      if (Array.isArray(result.otherHouseholdMembers)) {
        parsedResult.otherHouseholdMembers_search =
          parsedResult.otherHouseholdMembers?.join(',');
      }

      Object.keys(result).forEach((key) => {
        if (key.startsWith(AMENITY_DETAILS_PREFIX)) {
          unitAmenityHeaders.add(key);
        }
      });

      parsedResult.texting = calculateTextingStatus(
        result.twoWayRecipientIds,
        conversationsDictionary,
      );

      Object.assign(
        parsedResult,
        paymentDisplayFields(paymentStatusesByUnitId[result.unitId]),
      );
      if (typeof parsedResult.rdRentalAssistanceRA === 'boolean') {
        parsedResult.rdRentalAssistanceRA = result.rdRentalAssistanceRA
          ? 'yes'
          : 'no';
      }
      Object.assign(parsedResult, adaDisplayFields(result));

      results.push(parsedResult);
    });

    unitAmenityHeaders.delete(undefined);
    unitAmenityHeaders = Array.from(unitAmenityHeaders);

    return { results, unitAmenityHeaders };
  }, [conversationsDictionary, units, paymentStatusesByUnitId]);
};
const useHeaders = ({
  isAffordable,
  isConventional,
  isMixed,
  isRDRAEnabled,
  hasRD,
  rdRents,
  filterOptions,
  unitAmenityHeaders,
  showTextingColumn,
  showAutopayColumn,
  showPADColumn,
  intl,
}) => {
  const flags = useAppFlags();

  const updateHardshipColumnForRD = flags.hotmaRd && hasRD;
  const showLeadSourcesColumn = flags.viewMultipleLeadSourcesResidents;
  const showADAColumns = flags.adaInfo;

  return useMemo(() => {
    const headers = [];
    HEADERS({
      isAffordable,
      isConventional,
      isMixed,
      isRDRAEnabled,
      rdRents,
      showTextingColumn,
      showAutopayColumn,
      showPADColumn,
      intl,
      manageRentRollBuildingsColumn: flags.manageRentRollBuildingsColumn,
      updateHardshipColumnForRD,
      showLeadSourcesColumn,
      showADAColumns,
    }).forEach((header) => {
      if (filterOptions[`${header.id}Options`])
        header.filterOptions = filterOptions[`${header.id}Options`];

      headers.push(header);

      if (
        header.id === 'baseFloorPlanMarketRent' && // previous header id
        unitAmenityHeaders?.length
      ) {
        unitAmenityHeaders.forEach((unitAmenityHeader) => {
          headers.push({
            id: unitAmenityHeader,
            label: `${unitAmenityHeader.replace(
              AMENITY_DETAILS_PREFIX,
              '',
            )} Unit Amenity`,
            dataType: 'number',
            sortable: true,
            searchable: true,
          });
        });
      }
    });

    adaptHeaders(headers, { flags });

    return headers;
  }, [
    isAffordable,
    isConventional,
    isMixed,
    isRDRAEnabled,
    rdRents,
    showTextingColumn,
    showAutopayColumn,
    showPADColumn,
    intl,
    flags.manageRentRollBuildingsColumn,
    flags.leslie,
    updateHardshipColumnForRD,
    showLeadSourcesColumn,
    showADAColumns,
    filterOptions,
    unitAmenityHeaders,
  ]);
};

export const buildPaymentStatusCell = ({
  isPaymentsLoading,
  isPaymentsError,
  value,
}) => {
  if (isPaymentsLoading) {
    return {
      variant: 'description',
      value: <Skeleton variant="rounded" width={32} height={18} />,
    };
  }
  if (isPaymentsError || value === undefined) {
    return { variant: 'onOff', value: undefined };
  }
  return { variant: 'onOff', value };
};

const useRows = ({
  sortedAndFilteredResults = [],
  unitAmenityHeaders = [],
  isAffordable,
  isMixed,
  isRDRAEnabled,
  rdRents,
  isPaymentsLoading = false,
  isPaymentsError = false,
}) => {
  return useMemo(() => {
    return sortedAndFilteredResults?.map(
      ({
        id,
        actualUa,
        amenityFees,
        applicant,
        applicantStatus,
        applicationId,
        applicantResidentId,
        balance,
        baseFloorPlanMarketRent,
        bedsBaths,
        chargedUnitFees,
        collectionNote,
        collectionNoteStartTime,
        daysVacant,
        floorPlan,
        floorPlanId,
        grossRentLimit,
        leaseEndDate,
        leaseStartDate,
        leasedRent,
        marketRent,
        moveInDate,
        moveInReady,
        moveOutDate,
        netMaxRent,
        nonOptionalCharge,
        otherHouseholdMembers,
        priorLeasedRent,
        quotingRent,
        resident,
        residentId,
        residentRent,
        scheduledMoveInDate,
        setAside,
        sqFt,
        status,
        subsidyRent,
        totalGrossRent,
        underEviction,
        unit,
        unitId,
        utilityAllowances,
        texting,
        hardshipExpirationDate,
        isTodayWithing45DaysPriorToHardshipExpirationDate,
        requestedHardshipExemption,
        isAutopayActive,
        isPADActive,
        rdRentalAssistanceRA,
        leadSources,
        ...rest
      }) => {
        return {
          id,
          unit: {
            variant: 'link',
            to: getUrlWithSelectedPropertyId(`/unit/${unitId}`),
            value: unit,
          },
          floorPlan: {
            variant: 'link',
            to: getUrlWithSelectedPropertyId(`/floorPlan/${floorPlanId}`),
            value: floorPlan,
          },
          buildingNumber: {
            variant:
              rest?.buildingNumber?.length > 10 ? 'description' : 'string',
            value: rest.buildingNumber || '—',
          },
          ada: {
            value: rest.ada,
          },
          accessibilityType: accessibilityTypeCell(rest.accessibilityType),
          bedsBaths: {
            variant: 'number',
            value: bedsBaths,
          },
          sqFt: {
            variant: 'number',
            value: sqFt || '0',
          },
          status: {
            value: status,
          },
          resident: {
            variant: 'link',
            to: getUrlWithSelectedPropertyId(`/resident/${residentId}`),
            value: resident !== '' && resident?.length ? resident : null,
          },
          otherHouseholdMembers: {
            variant:
              otherHouseholdMembers?.length > 2 ? 'description' : undefined,
            value: otherHouseholdMembers?.length ? otherHouseholdMembers : null,
          },
          leadSources: {
            variant: 'string',
            value: leadSources,
          },
          texting: texting?.TOTAL
            ? {
                variant: 'texting',
                value: { ...texting },
                to: getUrlWithSelectedPropertyId(
                  `/resident/${residentId}?tab=${TAB_KEYS.TEXTING}`,
                ),
              }
            : { value: '' },
          ...((isAffordable || isMixed) && {
            hardshipExpirationDate: {
              variant: 'date',
              value: hardshipExpirationDate,
              iconName: 'AlertInfoIcon',
              sx: {
                color: isTodayWithing45DaysPriorToHardshipExpirationDate
                  ? 'error.main'
                  : 'inherit',
              },
            },
            requestedHardshipExemption: {
              variant: 'boolean',
              value: requestedHardshipExemption,
              iconName: 'CheckIcon',
              fontSize: 'large',
            },
            setAside: {
              variant: 'string',
              value: setAside,
            },
            ...(isRDRAEnabled && {
              rdRentalAssistanceRA: {
                variant: 'string',
                value: rdRentalAssistanceRA === 'yes' ? 'RA' : '',
              },
            }),
          }),
          moveInDate: {
            variant: 'date',
            value: moveInDate,
          },
          leaseStartDate: {
            variant: 'date',
            value: leaseStartDate,
          },
          leaseEndDate: {
            variant: 'date',
            value: leaseEndDate,
          },
          collectionNote: {
            variant: 'description',
            value: collectionNote
              ? [
                  formatDateDisplayLocal(collectionNoteStartTime),
                  collectionNote,
                ]
              : null,
          },
          underEviction: {
            variant: 'boolean',
            value: underEviction,
            iconName: 'AlertInfoIcon',
            color: 'error',
          },
          baseFloorPlanMarketRent: {
            variant: 'currency',
            value: baseFloorPlanMarketRent,
          },
          ...(unitAmenityHeaders?.length &&
            unitAmenityHeaders.reduce((object, unitAmenityHeaderId) => {
              object[unitAmenityHeaderId] = {
                variant: 'currency',
                value: rest[unitAmenityHeaderId],
              };

              return object;
            }, {})),
          amenityFees: {
            variant: 'currency',
            value: amenityFees,
          },
          marketRent: {
            variant: 'currency',
            value: marketRent,
          },
          ...((isAffordable || isMixed) && {
            totalGrossRent: {
              variant: 'currency',
              value: totalGrossRent,
            },
            grossRentLimit: {
              variant: 'currency',
              value: grossRentLimit,
            },
            netMaxRent: {
              variant: 'currency',
              value: netMaxRent,
            },
          }),
          priorLeasedRent: {
            variant: 'currency',
            value: priorLeasedRent,
          },
          isAutopayActive: buildPaymentStatusCell({
            isPaymentsLoading,
            isPaymentsError,
            value: isAutopayActive,
          }),
          isPADActive: buildPaymentStatusCell({
            isPaymentsLoading,
            isPaymentsError,
            value: isPADActive,
          }),
          leasedRent: {
            variant: 'currency',
            value: leasedRent,
          },
          ...((isAffordable || isMixed) && {
            nonOptionalCharge: {
              variant: 'currency',
              value: nonOptionalCharge,
            },
            actualUa: {
              variant: 'currency',
              value: actualUa,
            },
            utilityAllowances: {
              variant: 'currency',
              value: utilityAllowances,
            },
          }),
          residentRent: { variant: 'currency', value: residentRent },
          subsidyRent: { variant: 'currency', value: subsidyRent },
          ...(rdRents?.noteRent && {
            noteRent: {
              variant: 'currency',
              value: rest?.noteRent,
            },
          }),
          ...(rdRents?.basicRent && {
            basicRent: {
              variant: 'currency',
              value: rest?.basicRent,
            },
          }),
          chargedUnitFees: {
            variant: 'currency',
            value: chargedUnitFees,
          },
          balance: {
            variant: 'currency',
            value: balance,
          },
          moveOutDate: {
            variant: 'date',
            value: moveOutDate,
          },
          moveInReady: {
            value: moveInReady
              ? moveInReady === 'Ready'
                ? moveInReady
                : formatDateDisplayLocal(moveInReady)
              : null,
          },
          daysVacant: {
            variant: 'number',
            value: daysVacant,
          },
          quotingRent: {
            variant: 'currency',
            value: quotingRent,
          },
          applicant: {
            variant: 'link',
            value: applicant !== '' ? applicant : undefined,
            to: getUrlWithSelectedPropertyId(
              applicantResidentId
                ? `/resident/${applicantResidentId}`
                : `/application/${applicationId}`,
            ),
          },
          applicantStatus: {
            value: applicantStatus,
          },
          scheduledMoveInDate: {
            variant: 'date',
            value: scheduledMoveInDate,
          },
        };
      },
    );
  }, [
    isAffordable,
    isMixed,
    isRDRAEnabled,
    rdRents,
    sortedAndFilteredResults,
    unitAmenityHeaders,
    isPaymentsLoading,
    isPaymentsError,
  ]);
};

const useCSVPDFExport = ({
  filteredHeaders,
  rows,
  propertyName,
  hasAnyFilters,
  results,
  footerRow,
  isPaymentsLoading = false,
}) => {
  // Prepare headers for CSV: wrap labels with commas in double quotes
  const processedHeaders = useMemo(
    () =>
      filteredHeaders.map((header) => {
        let label =
          typeof header.label === 'string'
            ? header.label
            : header.label?.props?.defaultMessage;
        if (label && label.includes(',')) {
          label = `"${label}"`;
        }
        return { ...header, label };
      }),
    [filteredHeaders],
  );

  const csvRows = useMemo(() => {
    if (!isPaymentsLoading) return rows;
    return rows.map((row) => ({
      ...row,
      isAutopayActive: { variant: 'string', value: '—' },
      isPADActive: { variant: 'string', value: '—' },
    }));
  }, [rows, isPaymentsLoading]);

  const onCSVButtonClick = useCallback(() => {
    const fileName = generateStandardFilename({
      type: 'rent-roll',
      descriptor: propertyName,
      extension: 'csv',
    });
    const floorPlanSummary = buildFloorPlanSummaryForCSVExport({ results });
    const { rentRollFooterRows, summaryFooterRows } = buildCSVFooterRows({
      footerRow,
      summaryRows: floorPlanSummary,
    });

    exportAndDownloadCSV({
      excludedHeaders: [],
      filteredHeaders: processedHeaders,
      fileName,
      rows: [...csvRows, ...rentRollFooterRows],
      appendixHeaders: FLOOR_PLAN_SUMMARY_CSV_HEADERS,
      appendixRows: summaryFooterRows,
      hasAnyFilters,
    });
  }, [
    processedHeaders,
    csvRows,
    footerRow,
    results,
    hasAnyFilters,
    propertyName,
  ]);

  return { onCSVButtonClick };
};
